import React, { useState, useEffect, useRef } from 'react';
import './BannerPage.css';
import { FaEdit, FaTrash, FaCloudUploadAlt } from 'react-icons/fa';
import { FiPlusCircle } from 'react-icons/fi';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify, confirm } from '../../context/FeedbackContext';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';
import HasPermi from '../../components/HasPermi';

const EMPTY_FORM = {
  title: '',
  description: '',
  image_url: '',
  link_url: '',
  link_type: '1',
  sort_order: '0',
  status: '1',
};

function BannerPage({ onNavigate }) {
  const { selectedRestaurant } = useRestaurant() || {};
  const selectedStoreRef = selectedRestaurant?.reference || '';

  const [banners, setBanners] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState({});
  const [addFormErrors, setAddFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [goToPage, setGoToPage] = useState('');
  const [uploading, setUploading] = useState(false);

  // 用于新增弹窗的本地文件预览和 file 对象
  const [addFile, setAddFile] = useState(null);
  const [addPreview, setAddPreview] = useState('');
  // 用于编辑弹窗
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState('');

  const addFileRef = useRef(null);
  const editFileRef = useRef(null);

  const itemsPerPage = 10;

  const handleNavClick = (item) => {
    if (onNavigate) onNavigate(getRouteFromItem(item));
  };

  // ── Fetch banners ──
  useEffect(() => {
    if (!selectedStoreRef) return;
    const timer = setTimeout(() => {
      fetchBanners();
    }, searchTerm ? 400 : 0);
    return () => clearTimeout(timer);
  }, [selectedStoreRef, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBanners = async () => {
    setIsLoading(true);
    try {
      const url = searchTerm
        ? buildApiUrl(`/tenant/banner/search?tenantReference=${selectedStoreRef}&searchTerm=${encodeURIComponent(searchTerm)}&fuzzy=true`)
        : buildApiUrl(`/tenant/banner/list?tenantReference=${selectedStoreRef}`);
      const res = await fetch(url, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200) {
        setBanners(data.data || []);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Failed to fetch banners', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── File upload helper ──
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(buildApiUrl('/file/upload'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (data.code === 200) {
      return data.url;
    }
    throw new Error(data.msg || 'Upload failed');
  };

  // ── File select handlers ──
  const getAcceptTypes = (linkType) => {
    return linkType === '2' ? 'video/*' : 'image/*';
  };

  const handleAddFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAddFile(file);
    setAddPreview(URL.createObjectURL(file));
    setAddFormErrors(prev => ({ ...prev, image_url: undefined }));
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditFile(file);
    setEditPreview(URL.createObjectURL(file));
    setEditFormErrors(prev => ({ ...prev, image_url: undefined }));
  };

  // ── Pagination ──
  const totalPages = Math.ceil(banners.length / itemsPerPage);
  const paginatedBanners = banners.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const showingFrom = banners.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, banners.length);

  // ── Add ──
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    setAddForm(prev => ({ ...prev, [name]: value }));
    setAddFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (form, file) => {
    const errors = {};
    if (!form.title?.trim()) errors.title = 'Title is required.';
    if (!form.image_url?.trim() && !file) errors.image_url = 'Please upload a file.';
    if (form.sort_order !== '' && (isNaN(Number(form.sort_order)) || Number(form.sort_order) < 0)) {
      errors.sort_order = 'Please enter a valid non-negative number.';
    }
    return errors;
  };

  const handleAddBanner = async () => {
    const errors = validateForm(addForm, addFile);
    setAddFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!selectedStoreRef) {
      notify('No store selected. Cannot add banner.', 'error');
      return;
    }

    setUploading(true);
    try {
      // 先上传文件获取 URL
      let fileUrl = addForm.image_url;
      if (addFile) {
        fileUrl = await uploadFile(addFile);
      }

      const res = await fetch(buildApiUrl(`/tenant/banner/create`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          tenantReference: selectedStoreRef,
          title: addForm.title.trim(),
          description: addForm.description.trim(),
          imageUrl: fileUrl,
          linkUrl: addForm.link_url.trim(),
          linkType: Number(addForm.link_type),
          sortOrder: Number(addForm.sort_order),
          status: Number(addForm.status),
        }),
      });
      const data = await res.json();
      if (data.code === 200) {
        setShowAddModal(false);
        setAddForm(EMPTY_FORM);
        setAddFile(null);
        setAddPreview('');
        fetchBanners();
      } else {
        notify('Failed to add banner: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      notify('Failed to add banner: ' + err.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  // ── Edit ──
  const handleEdit = (banner) => {
    setEditForm({
      reference: banner.reference,
      title: banner.title || '',
      description: banner.description || '',
      image_url: banner.imageUrl || '',
      link_url: banner.linkUrl || '',
      link_type: String(banner.linkType ?? 1),
      sort_order: String(banner.sortOrder ?? 0),
      status: String(banner.status ?? 1),
    });
    setEditFile(null);
    setEditPreview('');
    setEditFormErrors({});
    setShowEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
    setEditFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSaveEdit = async () => {
    const errors = validateForm(editForm, editFile);
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setUploading(true);
    try {
      let fileUrl = editForm.image_url;
      if (editFile) {
        fileUrl = await uploadFile(editFile);
      }

      const res = await fetch(buildApiUrl(`/tenant/banner/update`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          reference: editForm.reference,
          tenantReference: selectedStoreRef,
          title: editForm.title.trim(),
          description: editForm.description.trim(),
          imageUrl: fileUrl,
          linkUrl: editForm.link_url.trim(),
          linkType: Number(editForm.link_type),
          sortOrder: Number(editForm.sort_order),
          status: Number(editForm.status),
        }),
      });
      const data = await res.json();
      if (data.code === 200) {
        setShowEditModal(false);
        setEditFile(null);
        setEditPreview('');
        fetchBanners();
      } else {
        notify('Failed to update banner: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      notify('Failed to update banner: ' + err.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  // ── Delete ──
  const handleDelete = async (banner) => {
    if (await confirm(`Are you sure you want to delete banner "${banner.title}"?`)) {
      try {
        const res = await fetch(buildApiUrl(`/tenant/banner/delete`), {
          method: 'POST',
          body: JSON.stringify({ reference: banner.reference }),
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        });
        const data = await res.json();
        if (data.code === 200) {
          fetchBanners();
        } else {
          notify('Failed to delete banner: ' + (data.msg || 'Unknown error'), 'error');
        }
      } catch (err) {
        notify('Failed to delete banner: ' + err.message, 'error');
      }
    }
  };

  // ── Helpers ──
  const getLinkTypeLabel = (type) => {
    const map = { 1: 'Image', 2: 'Video' };
    return map[type] || 'Unknown';
  };

  const isVideoUrl = (url) => {
    if (!url) return false;
    return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);
  };

  // ── 渲染上传区域 ──
  const renderUploadArea = (preview, existingUrl, fileRef, linkType, error, onFileChange) => {
    const displayUrl = preview || existingUrl;
    const isVideo = linkType === '2' || isVideoUrl(displayUrl);

    return (
      <>
        <div className={`banner-upload-area${error ? ' upload-error' : ''}`}
          onClick={() => fileRef.current?.click()}>
          {displayUrl ? (
            isVideo ? (
              <video src={displayUrl} className="banner-upload-preview" controls />
            ) : (
              <img src={displayUrl} alt="preview" className="banner-upload-preview" />
            )
          ) : (
            <div className="banner-upload-placeholder">
              <FaCloudUploadAlt className="banner-upload-icon" />
              <span>{linkType === '2' ? 'Click to upload video' : 'Click to upload image'}</span>
            </div>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept={getAcceptTypes(linkType)}
          className="banner-file-input"
          onChange={onFileChange}
        />
        {error && <p className="field-error">{error}</p>}
      </>
    );
  };

  // ── Pagination buttons ──
  const renderPageButtons = () => {
    const buttons = [];
    buttons.push(
      <button key="prev" className={`pg-btn${currentPage === 1 ? ' disabled' : ''}`}
        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}>Previous</button>
    );
    const start = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
    const end = Math.min(totalPages, start + 2);
    buttons.push(<span key="dots-start" className="pg-dots" style={{ visibility: start > 1 ? 'visible' : 'hidden' }}>...</span>);
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button key={i} className={`pg-btn${currentPage === i ? ' active' : ''}`}
          onClick={() => setCurrentPage(i)}>{i}</button>
      );
    }
    buttons.push(<span key="dots-end" className="pg-dots" style={{ visibility: end < totalPages ? 'visible' : 'hidden' }}>...</span>);
    buttons.push(
      <button key="next" className={`pg-btn${currentPage === totalPages ? ' disabled' : ''}`}
        onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}>Next</button>
    );
    return buttons;
  };

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="bannerpage-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li key={item} className={item === 'Banner' ? 'active' : ''}
              onClick={() => handleNavClick(item)}>
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>

      <main className="banner-content">
        <div className="admin-breadcrumb">
          <span className="breadcrumb-link" onClick={() => handleNavClick('Home')}>Home</span>
          <span className="breadcrumb-sep">&rsaquo;</span>
          <span className="breadcrumb-current">Banner Management</span>
        </div>
        <h1 className="admin-page-title">Banner Management</h1>
        <p className="admin-page-subtitle">Manage banners — create, edit, enable/disable and remove.</p>

        {/* Table */}
        <HasPermi permission={['banner:list', 'banner:search']} any>
          <div className="banner-table-card">
            <div className="banner-table-header">
              <h2 className="banner-table-title">Banner List</h2>
              <div className="banner-table-actions">
                <div className="search-box">
                  <input type="text" placeholder="Search by title..."
                    value={searchTerm}
                    onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                </div>
                <HasPermi permission="banner:create">
                  <button className="add-banner-btn"
                    onClick={() => {
                      setShowAddModal(true);
                      setAddForm(EMPTY_FORM);
                      setAddFormErrors({});
                      setAddFile(null);
                      setAddPreview('');
                    }}>
                    <FiPlusCircle /> Add Banner
                  </button>
                </HasPermi>
              </div>
            </div>

            <table className="banner-table">
              <thead>
                <tr>
                  <th>MEDIA</th>
                  <th>TITLE</th>
                  <th>DESCRIPTION</th>
                  <th>TYPE</th>
                  <th>SORT</th>
                  <th>STATUS</th>
                  <th>CREATED AT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>Loading...</td></tr>
                ) : paginatedBanners.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>No banners found.</td></tr>
                ) : paginatedBanners.map(banner => (
                  <tr key={banner.id}>
                    <td>
                      {banner.imageUrl ? (
                        isVideoUrl(banner.imageUrl) ? (
                          <video src={banner.imageUrl} className="banner-thumb" />
                        ) : (
                          <img src={banner.imageUrl} alt={banner.title} className="banner-thumb" />
                        )
                      ) : (
                        <span className="banner-no-img">-</span>
                      )}
                    </td>
                    <td>{banner.title}</td>
                    <td className="banner-desc-cell">{banner.description || '-'}</td>
                    <td>
                      <span className={`banner-type-badge ${banner.linkType === 1 ? 'type-image' : 'type-video'}`}>
                        {getLinkTypeLabel(banner.linkType)}
                      </span>
                    </td>
                    <td>{banner.sortOrder}</td>
                    <td>
                      <span className={`status-badge ${banner.status === 1 ? 'enabled' : 'disabled'}`}>
                        {banner.status === 1 ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td>{banner.createdAt}</td>
                    <td className="actions">
                      <HasPermi permission="banner:update">
                        <button className="edit-btn" onClick={() => handleEdit(banner)}><FaEdit /></button>
                      </HasPermi>
                      <HasPermi permission="banner:delete">
                        <button className="delete-btn" onClick={() => handleDelete(banner)}><FaTrash /></button>
                      </HasPermi>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination-row">
              <span className="pagination-info">Showing {showingFrom} to {showingTo} of {banners.length} results</span>
              <div className="pagination-buttons">{renderPageButtons()}</div>
              <div className="pagination-goto">
                <span>Go to page</span>
                <input className="pg-goto-input" type="number" min="1" max={totalPages} value={goToPage}
                  onChange={e => setGoToPage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { const p = parseInt(goToPage); if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); } } }}
                  placeholder="" />
                <button className="pg-btn" onClick={() => { const p = parseInt(goToPage); if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); } }}>Go</button>
              </div>
            </div>
          </div>
        </HasPermi>

        <HasPermi
          permission={['banner:list', 'banner:search']}
          any
          fallback={<div className="no-permission-message"><p>You don't have permission to view banners.</p></div>}
        />
      </main>

      {/* Add Modal */}
      <HasPermi permission="banner:create">
        {showAddModal && (
          <div className="admin-modal-overlay">
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <button className="admin-modal-close" onClick={() => setShowAddModal(false)}>&times;</button>
                <h2 className="admin-modal-title">Add Banner</h2>
                <p className="admin-modal-subtitle">Create a new banner. Fill in all required fields.</p>
              </div>
              <div className="admin-modal-body">
                <div className="form-field">
                  <label>Title <span className="required">*</span></label>
                  <input className={addFormErrors.title ? 'input-error' : ''} name="title" value={addForm.title}
                    onChange={handleAddInputChange} placeholder="e.g. Summer Sale" />
                  {addFormErrors.title && <p className="field-error">{addFormErrors.title}</p>}
                </div>
                <div className="form-field">
                  <label>Description</label>
                  <input name="description" value={addForm.description}
                    onChange={handleAddInputChange} placeholder="e.g. Up to 50% off" />
                </div>
                <div className="form-field">
                  <label>Media Type <span className="required">*</span></label>
                  <select name="link_type" value={addForm.link_type} onChange={(e) => {
                    handleAddInputChange(e);
                    // 切换类型时清空已选文件
                    setAddFile(null);
                    setAddPreview('');
                  }}>
                    <option value="1">Image</option>
                    <option value="2">Video</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>{addForm.link_type === '2' ? 'Video' : 'Image'} <span className="required">*</span></label>
                  {renderUploadArea(addPreview, addForm.image_url, addFileRef, addForm.link_type, addFormErrors.image_url, handleAddFileChange)}
                </div>
                <div className="form-field">
                  <label>Link URL</label>
                  <input name="link_url" value={addForm.link_url}
                    onChange={handleAddInputChange} placeholder="e.g. /products or https://example.com" />
                </div>
                <div className="form-field">
                  <label>Sort Order</label>
                  <input name="sort_order" type="number" min="0" value={addForm.sort_order}
                    onChange={handleAddInputChange} placeholder="e.g. 0" />
                  {addFormErrors.sort_order && <p className="field-error">{addFormErrors.sort_order}</p>}
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <select name="status" value={addForm.status} onChange={handleAddInputChange}>
                    <option value="1">Enabled</option>
                    <option value="0">Disabled</option>
                  </select>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="cancel-btn" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button className="submit-btn" onClick={handleAddBanner} disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Add Banner'}
                </button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>

      {/* Edit Modal */}
      <HasPermi permission="banner:update">
        {showEditModal && (
          <div className="admin-modal-overlay">
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <button className="admin-modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
                <h2 className="admin-modal-title">Edit Banner</h2>
                <p className="admin-modal-subtitle">Update the banner information.</p>
              </div>
              <div className="admin-modal-body">
                <div className="form-field">
                  <label>Title <span className="required">*</span></label>
                  <input className={editFormErrors.title ? 'input-error' : ''} name="title" value={editForm.title || ''}
                    onChange={handleEditInputChange} placeholder="e.g. Summer Sale" />
                  {editFormErrors.title && <p className="field-error">{editFormErrors.title}</p>}
                </div>
                <div className="form-field">
                  <label>Description</label>
                  <input name="description" value={editForm.description || ''}
                    onChange={handleEditInputChange} placeholder="e.g. Up to 50% off" />
                </div>
                <div className="form-field">
                  <label>Media Type <span className="required">*</span></label>
                  <select name="link_type" value={editForm.link_type ?? '1'} onChange={(e) => {
                    handleEditInputChange(e);
                    setEditFile(null);
                    setEditPreview('');
                  }}>
                    <option value="1">Image</option>
                    <option value="2">Video</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>{editForm.link_type === '2' ? 'Video' : 'Image'} <span className="required">*</span></label>
                  {renderUploadArea(editPreview, editForm.image_url, editFileRef, editForm.link_type, editFormErrors.image_url, handleEditFileChange)}
                </div>
                <div className="form-field">
                  <label>Link URL</label>
                  <input name="link_url" value={editForm.link_url || ''}
                    onChange={handleEditInputChange} placeholder="e.g. /products or https://example.com" />
                </div>
                <div className="form-field">
                  <label>Sort Order</label>
                  <input name="sort_order" type="number" min="0" value={editForm.sort_order ?? '0'}
                    onChange={handleEditInputChange} placeholder="e.g. 0" />
                  {editFormErrors.sort_order && <p className="field-error">{editFormErrors.sort_order}</p>}
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <select name="status" value={editForm.status ?? '1'} onChange={handleEditInputChange}>
                    <option value="1">Enabled</option>
                    <option value="0">Disabled</option>
                  </select>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="cancel-btn" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button className="submit-btn" onClick={handleSaveEdit} disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>
    </div>
  );
}

export default BannerPage;